// @flow

import React from 'react';
import styled, { withTheme } from 'styled-components';
import { colors, fontWeight, buildVersionSelector } from '../constants';
import PrimaryPalette from '../../birch/colors';
import type { ReskinThemeType } from '../constants';

type Option = {
  value: string | number | boolean,
  label: string,
};

type Props = {
  options: Array<Option>,
  selected: string | number | boolean,
  onChange: (any) => void,
  width?: string,
  segmentPadding?: string,
  disabled?: boolean,
  theme?: { name: ReskinThemeType },
};

const azaleaVersion = {
  enabled: {
    unselected: {
      borderColor: colors.lightGrey,
      textColor: colors.mediumGrey,
    },
    selected: {
      textColor: colors.darkestGrey,
      borderColor: colors.mediumGreenhouseGreen,
    },
  },
  disabled: {
    unselected: {
      backgroundColor: colors.lightestGrey,
    },
    selected: {
      borderColor: colors.mediumGrey,
      textColor: colors.mediumGrey,
    },
  },
};

const birchVersion = {
  enabled: {
    unselected: {
      borderColor: PrimaryPalette.darkerGrey,
      textColor: PrimaryPalette.darkerGrey,
    },
    selected: {
      textColor: PrimaryPalette.mediumEvergreen,
      borderColor: PrimaryPalette.darkGreen,
    },
  },
  disabled: {
    unselected: {
      backgroundColor: PrimaryPalette.lightGrey,
    },
    selected: {
      borderColor: PrimaryPalette.darkerGrey,
      textColor: PrimaryPalette.darkerGrey,
    },
  },
};

const versionSelector = buildVersionSelector(azaleaVersion, birchVersion);

class SegmentedControl extends React.PureComponent<Props> {
  static defaultProps = {
    width: '100%',
    segmentPadding: '10px',
    disabled: false,
  };

  render() {
    const { width, options, onChange, segmentPadding, disabled } = this.props;
    const selected = this.props.selected
      ? this.props.selected
      : options[0].value;

    return (
      <Control width={width} className="segmented-controls">
        {options.map((option) => (
          <Item
            key={option.label}
            disabled={disabled}
            className="segmented-control"
            selected={selected === option.value}
            onClick={() => {
              if (!disabled) {
                onChange(option.value);
              }
            }}
            segmentPadding={segmentPadding}
          >
            {option.label}
          </Item>
        ))}
      </Control>
    );
  }
}

/* $FlowFixMe This comment suppresses an error found when upgrading Flow to
 * v0.92.0. To view the error, delete this comment and run Flow. */
const Control = styled.div`
  ${fontWeight('normal')}
  width: ${(props) => props.width};
  display: table;
  table-layout: fixed;
`;

const Item = styled.div`
  display: table-cell;
  padding: ${(props) => props.segmentPadding};
  cursor: pointer;
  text-align: center;

  ${(props) => `
  border: 1px solid
    ${versionSelector(props.theme?.name).enabled.unselected.borderColor};
  color: ${versionSelector(props.theme?.name).enabled.unselected.textColor};`}

  ${(props) =>
    props.disabled &&
    `
    background-color: ${
      versionSelector(props.theme?.name).disabled.unselected.backgroundColor
    };
  `}

  ${(props) =>
    props.selected &&
    `
    & + div {
      border-left: 1px solid transparent;
    }
  `}

  ${(props) =>
    props.selected &&
    !props.disabled &&
    `
    color: ${versionSelector(props.theme?.name).enabled.selected.textColor};
    border: 2px solid ${
      versionSelector(props.theme?.name).enabled.selected.borderColor
    };
  `}

  ${(props) =>
    props.selected &&
    props.disabled &&
    `
    color: ${versionSelector(props.theme?.name).disabled.selected.textColor};
    border: 2px solid ${
      versionSelector(props.theme?.name).disabled.selected.borderColor
    };
  `}

  &:not(:last-child) {
    ${(props) =>
      !props.selected &&
      `
      border-right: 1px solid transparent;
    `}
  }
`;
Item.displayName = 'Item';

export default withTheme(SegmentedControl);
